import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { IApiLineupPlayer } from '@trackman/baseball-shared-components';
import { ReactComponent as CloseIcon } from 'components/icons/close.svg';
import Autosuggest, {
  ChangeEvent,
  RenderSuggestionParams,
  SuggestionSelectedEventData,
  SuggestionsFetchRequestedParams,
} from 'react-autosuggest';
import { ReactComponent as SearchIcon } from 'components/icons/search.svg';
import styled from 'styled-components';
import { BaseRosterWrapper, NoPlayers, Player, RosterContent, Title } from './styles/BaseRosterStyles';

interface IProps {
  players: IApiLineupPlayer[];
  onPlayerClick: (player: IApiLineupPlayer) => void;
  onClose: () => void;
  unavailablePlayers: string[];
}

const BaseRoster: React.FC<IProps> = (props) => {
  const { players, onPlayerClick, onClose, unavailablePlayers } = props;
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [playerList, setPlayerList] = useState<IApiLineupPlayer[]>(players);
  const ref = useRef<any>(null);
  const [maxRosterHeight, setMaxRosterHeight] = useState(320);
  const rosterContentRef = useRef<any>(null);
  const titleRef = useRef<any>();

  const inputProps = {
    value: searchQuery,
    placeholder: 'Search to add player',
    onChange: (event: React.FormEvent, params: ChangeEvent) => setSearchQuery(params.newValue),
    autoFocus: true,
    ref,
  };

  const focusSearchBar = () => {
    if (ref.current) {
      ref.current.focus();
    }
  };

  const renderSuggestion = (suggestion: IApiLineupPlayer, params: RenderSuggestionParams) => {
    const { nameRef } = suggestion;

    return <Player onClick={(): void => onPlayerClick({} as any)}>{nameRef}</Player>;
  };

  const onSuggestionSelected = (event: FormEvent<any>, data: SuggestionSelectedEventData<any>) => {
    onPlayerClick(data.suggestion);
  };

  const renderInputComponent = (props: any) => (
    <div id='search-bar'>
      <span>
        <SearchIcon className={`${searchQuery.length > 0 ? 'active' : ''}`} />
      </span>
      <input {...props} />
    </div>
  );

  const onSuggestionsFetchRequested = (params: SuggestionsFetchRequestedParams) => {
    setPlayerList(() => {
      return players.filter(
        (player) =>
          player.nameRef.toLowerCase().includes(params.value.toLowerCase()) &&
          !unavailablePlayers?.includes(player.id) &&
          !unavailablePlayers?.includes(player.nameRef)
      );
    });
  };

  const onSuggestionsClearRequested = () => {
    setPlayerList(players);
  };

  const handleMaxRosterHeight = () => {
    const rosterContentHeight = rosterContentRef?.current.getBoundingClientRect().height;
    const titleHeight = titleRef?.current.getBoundingClientRect().height;
    const searchBarHeight = document.querySelector('#roster-content #search-bar')?.getBoundingClientRect().height;

    if (rosterContentHeight && titleHeight && searchBarHeight) {
      setMaxRosterHeight(rosterContentHeight - titleHeight - searchBarHeight);
    }
  };

  const renderSuggestionsContainer = ({ containerProps, children, query }: any) => {
    if (children) {
      return <div {...containerProps}>{children}</div>;
    }

    return (
      <NoPlayers {...containerProps} className={`${containerProps.className}`}>
        No player(s) found
      </NoPlayers>
    );
  };

  useEffect(() => {
    setPlayerList(() => {
      return players.filter((player) => !unavailablePlayers?.includes(player.id) && !unavailablePlayers?.includes(player.nameRef));
    });
  }, [unavailablePlayers]);

  useEffect(() => {
    handleMaxRosterHeight();
    window.addEventListener('resize', handleMaxRosterHeight, false);

    return () => {
      window.removeEventListener('resize', handleMaxRosterHeight);
    };
  }, []);

  return (
    <BaseRosterWrapper onClick={focusSearchBar}>
      <RosterContent id='roster-content' ref={rosterContentRef}>
        <Title ref={titleRef}>
          <div>Select a player from your roster</div>
          <CloseIcon onClick={(): void => onClose()} />
        </Title>
        <AutosuggestStyleWrapper maxHeight={maxRosterHeight}>
          <Autosuggest
            suggestions={playerList}
            onSuggestionsFetchRequested={onSuggestionsFetchRequested}
            onSuggestionsClearRequested={onSuggestionsClearRequested}
            getSuggestionValue={(suggestion) => suggestion.nameRef}
            renderSuggestion={renderSuggestion}
            inputProps={inputProps}
            onSuggestionSelected={onSuggestionSelected}
            renderInputComponent={renderInputComponent}
            alwaysRenderSuggestions={searchQuery.length === 0}
            renderSuggestionsContainer={renderSuggestionsContainer}
          />
        </AutosuggestStyleWrapper>
      </RosterContent>
    </BaseRosterWrapper>
  );
};

export default BaseRoster;

const AutosuggestStyleWrapper = styled.div<{ maxHeight?: number }>`
  .react-autosuggest__suggestions-container {
    height: ${(props) => props.maxHeight}px;
    overflow-y: scroll;
  }
`;
